const DB_NAME = 'ourtake-offline';
const STORE_NAME = 'contributions';
// Bumped to 2 to add createdAt index for FIFO sync ordering
const DB_VERSION = 2;

export interface QueuedContributionError {
  code: string;
  message: string;
  at: string; // ISO timestamp
}

export interface QueuedContribution {
  id: string;
  threadId: string;
  contributionType: 'text' | 'photo' | 'audio' | 'video';
  content?: string;
  mediaBlob?: Blob;
  mediaFilename?: string;
  mediaContentType?: string;
  mediaDuration?: number;
  mediaThumbnailBlob?: Blob;
  mediaThumbnailFilename?: string;
  mediaThumbnailContentType?: string;
  mediaWidth?: number;
  mediaHeight?: number;
  visibility?: 'public' | 'private';
  buildsOnContributionId?: string;
  memoryDate?: string;
  metadata?: Record<string, unknown>;
  // Mood Ring arc selection carried on a first take so it locks server-side as a
  // step of contributing (#545) — matters offline because the arc is no longer
  // locked up front, so a pick-then-offline first take must replay it or the sync
  // is rejected for a missing arc. Best-effort server-side; ignored once set.
  arcShape?: string;
  createdAt: string;
  synced: boolean;
  // Set when the last replay attempt failed with a non-transient error (e.g.
  // backend template gate rejection). Surfaced in the UI so the user can
  // recover instead of silently re-queueing forever.
  lastError?: QueuedContributionError | null;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('threadId', 'threadId', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      } else {
        // Upgrading from v1: add the createdAt index if missing
        const tx = request.transaction!;
        const store = tx.objectStore(STORE_NAME);
        if (!store.indexNames.contains('createdAt')) {
          store.createIndex('createdAt', 'createdAt', { unique: false });
        }
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function addToQueue(contribution: QueuedContribution): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(contribution);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Returns unsynced contributions eligible for replay, sorted by createdAt (FIFO).
 * Items with a `lastError` are excluded — those represent persistent backend
 * rejections that should not be retried automatically (they are surfaced via
 * `getFailed()` for the UI to recover). Re-attempting them on every sync cycle
 * would otherwise loop forever, emitting duplicate `st:queue-failure` events.
 */
export async function getUnsynced(): Promise<QueuedContribution[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);

    const isReplayable = (item: QueuedContribution) =>
      item.synced === false && item.lastError == null;

    // Use the createdAt index to iterate in chronological order,
    // filtering to unsynced entries only.
    if (store.indexNames.contains('createdAt')) {
      const results: QueuedContribution[] = [];
      const index = store.index('createdAt');
      if (typeof index.openCursor === 'function') {
        const cursorReq = index.openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (cursor) {
            if (isReplayable(cursor.value)) {
              results.push(cursor.value);
            }
            cursor.continue();
          } else {
            resolve(results);
          }
        };
        cursorReq.onerror = () => reject(cursorReq.error);
      } else if (typeof index.getAll === 'function') {
        const request = index.getAll();
        request.onsuccess = () => {
          const items = (request.result as QueuedContribution[]).filter(isReplayable);
          items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
          resolve(items);
        };
        request.onerror = () => reject(request.error);
      } else {
        resolve([]);
      }
    } else {
      // Fallback for pre-v2 databases: fetch all unsynced and sort in memory
      const index = store.index('synced');
      const request = index.getAll(IDBKeyRange.only(false));
      request.onsuccess = () => {
        const items = (request.result as QueuedContribution[]).filter(isReplayable);
        items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        resolve(items);
      };
      request.onerror = () => reject(request.error);
    }
  });
}

export async function markSynced(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      if (getReq.result) {
        store.put({ ...getReq.result, synced: true });
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function markFailed(id: string, error: QueuedContributionError): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      if (getReq.result) {
        store.put({ ...getReq.result, lastError: error });
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Returns unsynced contributions whose last replay attempt failed with a
 * non-transient error (e.g. backend template gate rejection). Surfaced to the
 * UI for manual recovery; these are intentionally excluded from `getUnsynced()`
 * to prevent an infinite retry loop on every sync cycle.
 */
export async function getFailed(): Promise<QueuedContribution[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('synced');
    const request = index.getAll(IDBKeyRange.only(false));
    request.onsuccess = () => {
      const items = (request.result as QueuedContribution[]).filter(
        (item) => item.lastError != null,
      );
      items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      resolve(items);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear `lastError` from every queued item for the given thread so they become
 * eligible for automatic replay again. Called after the user recovers from a
 * template gate (assigns a role / locks an arc) so previously-failed offline
 * contributions are no longer permanently stuck. Returns the number of items
 * that had their error cleared.
 */
export async function clearFailedForThread(threadId: string): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('threadId');
    const request = index.getAll(IDBKeyRange.only(threadId));
    let cleared = 0;
    request.onsuccess = () => {
      const items = (request.result as QueuedContribution[]).filter(
        (item) => item.lastError != null,
      );
      for (const item of items) {
        store.put({ ...item, lastError: null });
        cleared++;
      }
    };
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve(cleared);
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Stamp `arcShape` onto every unsynced queued item for the thread. Needed when
 * the user picks a Mood Ring arc *after* a take was already queued (#545): the
 * arc is only locked as a step of contributing now, so an item queued before the
 * pick carries no `arcShape` and its replay fails the arc gate forever —
 * `clearFailedForThread` clears the error marker but cannot supply the arc.
 * Stamped onto every unsynced item rather than just the oldest so the arc lands
 * whichever one syncs first; the server ignores `arcShape` once the arc is set.
 * Returns the number of items stamped.
 */
export async function setArcShapeForThread(threadId: string, arcShape: string): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('threadId');
    const request = index.getAll(IDBKeyRange.only(threadId));
    let stamped = 0;
    request.onsuccess = () => {
      const items = (request.result as QueuedContribution[]).filter(
        (item) => !item.synced && item.arcShape !== arcShape,
      );
      for (const item of items) {
        store.put({ ...item, arcShape });
        stamped++;
      }
    };
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve(stamped);
    tx.onerror = () => reject(tx.error);
  });
}

export async function removeFromQueue(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
